/**
 * @author Sarah Nouh
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-12-30 11:41:36
 * @description Implementation of the search results grid
 * @filename search-grid.tsx
 */
import { Edge as GraphQlEdge } from "@radivision/graphql/lib/ts/graphql/edge";
import { SearchFacet as GraphQlSearchFacet, HitItemKind } from "@radivision/graphql/lib/ts/graphql/search-hit";
import React from "react";
import { CarouselItem } from "../../component-configuration/carousel-item";
import { getCarouselItem as _getCarouselItem } from "../../utilities/general";
import { Router } from "../../utilities/router";
import { FILTERS, search as _search, ODRED_KIND } from "../../utilities/search";
import { Loader } from "../page/loader";
import { GridView } from "./grid-view";
import { PreviewKind } from "@radivision/graphql/lib/ts/graphql/preview-type";
import { SVG } from "../../component-configuration/svgs";
import { ArraysHelper } from "../../utilities/arrays";
const windowSize = window.innerWidth;
const debounce = require("lodash.debounce");

/**
 * The props of the Modal class.
 *
 * @interface
 */
interface FiltersArray {
  /**
   *The name of the filter
   *
   * @type {string}
   */
  name: string;

  /**
   * the kind of this filter e.g: GRAPHQL_TYPE_ARTICLE_STORY, GRAPHQL_TYPE_PODCAST_STORY, ...etc
   *
   * @type {string}
   */
  kind: string;

  /**
   * The number of results associated with that filter
   *
   * @type {number}
   */
  count: number;
}

/**
 * The state of the Search result grid component.
 *
 * @interface
 */
interface SearchResultsGridState {
  /**
   * The active state of filters associated with the search
   *
   * @type {string[]}
   */
  activeFilter: string[];

  /**
   * all search results items
   *
   * @type {CarouselItem[]}
   */
  allSearchResults: CarouselItem[];

  /**
   * a loading state to toggle between loading spinner or the search results
   *
   * @type {boolean}
   */
  loading: boolean;

  /**
   * The search Result with the 2D array format to be ready for display.
   *
   * @type {CarouselItem[][]}
   */
  searchResultsFormatted: CarouselItem[][];
  /**
   * The other related search suggestions
   *
   * @type {string[]}
   */
  searchSuggestions: string[];
  /**
   * The search value typed in
   *
   * @type {string}
   */
  searchValue: string;
  /**
   * The number of results associated with the filters on the side after filtering
   *
   * @type {number}
   */
  filteredResultsCount: string;
  /**
   * A boolean to indicate loading more results
   *
   * @type {boolean}
   */
  showMoreResults: boolean;

  /**
   * @type {string}
   */
  orderSearchBy: string;

  /**
   *
   *
   * @type {""}
   * @memberof searchResultsGridState
   */
  feedBackMessage: string;
}

/**
 * A React component that renders the search results grids.
 *
 * @extends {React.Component<{}>}
 */
export class SearchResultsGrid extends React.Component<{}, SearchResultsGridState> {
  /**
   *
   *
   * @type {boolean}
   * @memberof SearchResultsGridState
   */
  pagination: { isOnGoing: boolean; noMore: boolean };

  /**
   * A helper array to set the state to indicate the current active filter.
   *
   * @type {string[]}
   */
  activeFilterStateArray: string[];
  /**
   * The current filters of the component.
   *
   * @type {string[]}
   */
  filters: FiltersArray[];
  /**
   * The associated search filter if the search was triggered within a panel
   *
   * @type {string}
   */
  panelFilter: string;

  /**
   * active tag filter value, if the search query url parameter tag exists
   *
   * @type {string}
   */
  tagFilter: string;

  /**
   * Constructor.
   *
   * @param {{}} props The props of the component.
   */
  constructor(props: {}) {
    super(props);
    // initializing values for the results filters
    this.handleSearchFilters();
    // initializing the active filter state
    this.activeFilterStateArray = [];
    for (let i = 0; i < this.filters.length; i++) {
      if (i === 0) {
        this.activeFilterStateArray[i] = "active";
      } else {
        this.activeFilterStateArray[i] = "";
      }
    }
    this.state = {
      activeFilter: [...this.activeFilterStateArray],
      allSearchResults: [],
      loading: true,
      searchValue: undefined,
      searchResultsFormatted: [],
      searchSuggestions: [],
      filteredResultsCount: "0",
      showMoreResults: false,
      feedBackMessage: "",
      orderSearchBy: undefined,
    };
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleSearchQuery = this.handleSearchQuery.bind(this);

    this.pagination = { isOnGoing: false, noMore: false };
  }

  /**
   *
   *
   * @memberof SearchResultsGrid
   */
  handleSearchFilters() {
    this.filters = [];

    return Router.getQueryParameters().then((res: string[]) => {
      this.setState({ searchValue: decodeURIComponent(res[0]).split("=")[1] });
      // if we have a panel filter retrieve it from the query parameters
      if (res[2] !== undefined) {
        this.panelFilter = decodeURIComponent(res[2]).split("=")[1];
        this.tagFilter = decodeURIComponent(res[3]).split("=")[1];
        // if the results are filtered by resources -> set resources filters
        if (this.panelFilter.search("resource") !== -1) {
          this.filters = [
            { name: "all resources", kind: "", count: 0 },
            { name: "articles", kind: HitItemKind.ARTICLE, count: 0 },
            { name: "books", kind: HitItemKind.BOOK, count: 0 },
            {
              name: "newsletters",
              kind: HitItemKind.NEWSLETTER,
              count: 0,
            },
            {
              name: "courses",
              kind: HitItemKind.ONLINE_COURSE,
              count: 0,
            },
            { name: "podcasts", kind: HitItemKind.PODCAST, count: 0 },
          ];
        }
      } else {
        // search is initiated from main navbarClick
        // therefore add basic filters
        this.panelFilter = "";
        this.tagFilter = "";
        this.filters = [
          { name: "all results", kind: "", count: 0 },
          {
            name: "shows",
            kind: HitItemKind.ORIGINAL_CONTENT,
            count: 0,
          },
          { name: "people", kind: HitItemKind.PERSON, count: 0 },
          { name: "companies", kind: HitItemKind.ENTITY, count: 0 },
          { name: "resources", kind: "resources", count: 0 },
        ];
      }
    });
  }

  /**
   * A function invoked when the component is mounted
   *
   * @type {function())}
   */
  componentDidMount() {
    this.handleSearchResults();
    window.addEventListener("hashchange", this.handleHashChange, false);
    window.addEventListener(
      "scroll",
      debounce(() => {
        if (window.innerHeight + document.documentElement.scrollTop === document.documentElement.offsetHeight) {
          if (!this.pagination.noMore) {
            if (!this.pagination.isOnGoing) {
              this.pagination.isOnGoing = true;
              this.handleSearchResults("PAGINATION").then(() => {
                this.pagination.isOnGoing = false;
                this.pagination.noMore = this.state.allSearchResults.length % 48 !== 0;
              });
            }
          }
        }
      }, 60),
    );
    this.activeFilterStateArray[0] = "active";
  }

  /**
   *
   *
   * @returns {Promise<any>}
   * @memberof SearchResultsGrid
   */
  handleSearchQuery(
    filters?: { field: string; values: string[] }[],
  ): Promise<{ searchItems?: CarouselItem[]; facets?: GraphQlSearchFacet[] }> {
    const CURSOR: string =
      this.state.allSearchResults && this.state.allSearchResults.length
        ? this.state.allSearchResults.length.toString()
        : undefined;
    return _search(this.state.searchValue, filters, "or", this.state.orderSearchBy, CURSOR).then(
      (result: { count: string; edges: GraphQlEdge[]; facets: GraphQlSearchFacet[] }) => {
        const results: {
          searchItems?: CarouselItem[];
          facets?: GraphQlSearchFacet[];
        } = {};

        results.searchItems =
          result && result.edges
            ? result.edges.map((edge) => {
                return _getCarouselItem(
                  edge,
                  null,
                  0,
                  {
                    containerWidthRatio: windowSize > 1920 ? 1.2 : 4 / 4,
                    numberOfItems: result.edges.length > 6 ? (windowSize > 800 ? 4 : 1) : result.edges.length,
                  },
                  PreviewKind.SQUARE,
                );
              })
            : null;

        results.facets = result.facets;

        return results;
      },
    );
  }

  /**
   * A function invoked to handle retrieving search suggestions, filters and results
   *
   * @type {function())}
   */
  handleSearchResults(source?: string) {
    let feedBackMessage: string = "";
    let updateFacets: boolean = true;
    this.setState({ loading: true });
    return Router.getQueryParameters().then((queryParameters) => {
      if (source === "FILTER" || source === "SORT") {
        // if we're searching from scratch clear the filters state
        // if the search function is not triggered from a filter click
        this.handleResetArray();
      }
      if (source !== undefined && source !== null) {
        updateFacets = false;
      }
      this.setState(
        {
          activeFilter: [...this.activeFilterStateArray],
          searchValue: decodeURIComponent(queryParameters[0]).split("=")[1],
          searchSuggestions: [],
        },
        () => {
          if (this.state.searchValue) {
            let activeFilter: any;

            if (this.state.activeFilter.indexOf("active") > 0) {
              activeFilter = this.filters[this.state.activeFilter.indexOf("active")].name;
            }

            if (activeFilter !== undefined && activeFilter !== null) {
              activeFilter = [
                {
                  field: "kind",
                  values: FILTERS[activeFilter],
                },
              ];
            } else {
              if (this.panelFilter !== undefined) {
                if (this.panelFilter.search("people") !== -1) {
                  activeFilter = [
                    {
                      field: "kind",
                      values: FILTERS["people"],
                    },
                  ];
                } else if (this.panelFilter.search("organization") !== -1) {
                  activeFilter = [
                    {
                      field: "kind",
                      values: FILTERS["companies"],
                    },
                  ];
                }
              }
            }
            let currentSearchValue = "";
            if (queryParameters[1]) {
              currentSearchValue = decodeURIComponent(queryParameters[1]).split("=")[1];
            }
            // // // suggestions are only filtered if the search is initiated from one of the panels
            // // getSuggestions(currentSearchValue, this.panelFilter).then(
            // //   (result) => {
            // //     let searchSuggestions: any;
            // //     if (this.state.searchValue.length > 0) {
            // //       searchSuggestions = result.map((suggestion) => {
            // //         const suggestionObject = {
            // //           title: suggestion.suggestion,
            // //           __typename: GRAPHQL_TYPE_SEARCH_HIT,
            // //         };
            // //         return _getCarouselItem(
            // //           suggestionObject,
            // //           null,
            // //           0,
            // //           {
            // //             containerWidthRatio: 1,
            // //             numberOfItems: 1,
            // //           },
            // //           PreviewKind.SQUARE
            // //         );
            // //       });
            // //     }
            // //     this.setState({
            // //       searchSuggestions: [...searchSuggestions],
            // //     });
            // //   }
            // // );

            return this.handleSearchQuery(activeFilter)
              .then((results: { searchItems?: CarouselItem[]; facets?: GraphQlSearchFacet[] }) => {
                if (results.searchItems.length === 0 && this.state.allSearchResults.length === 0) {
                  feedBackMessage = "No Results Found";
                }
                // check needed so that data is not rendered after clearing search value
                if (results.searchItems.length) {
                  // items coming back from search function to be populated.
                  // let tagSearchResults: CarouselItem[] = [...searchResults];
                  // if (this.tagFilter) {
                  //   tagSearchResults = searchResults.filter((item) =>
                  //     this.includeItemOnFilter(item)
                  //   );
                  // }

                  // save counters for all results and all resources results respectively
                  const isResourcesSearch: boolean = this.panelFilter
                    ? this.panelFilter.search("resource") !== -1
                    : false;

                  if (updateFacets) {
                    const COUNT_FACET = results.facets.find((facet) => {
                      return facet.name === "kind";
                    });

                    // get counters for each type
                    const filtersCount: { [index: string]: number } = {};
                    let totalCount = 0;
                    if (COUNT_FACET && COUNT_FACET.buckets) {
                      COUNT_FACET.buckets.forEach((bucket) => {
                        filtersCount[bucket.value] = bucket.count;
                        totalCount += bucket.count;
                      });
                    }

                    const peopleResults: number = filtersCount[HitItemKind.PERSON]
                      ? filtersCount[HitItemKind.PERSON]
                      : 0;
                    const entityResults: number = filtersCount[HitItemKind.ENTITY]
                      ? filtersCount[HitItemKind.ENTITY]
                      : 0;

                    const PEOPLE_ENTITY = peopleResults + entityResults;

                    const resourcesCount: number = isNaN(PEOPLE_ENTITY) ? totalCount : totalCount - PEOPLE_ENTITY;

                    // now save these results to the filters array
                    this.filters = this.filters.map((filter: FiltersArray) => {
                      return {
                        ...filter,
                        count:
                          filter.kind === "" && isResourcesSearch
                            ? resourcesCount
                            : filter.kind === ""
                            ? totalCount
                            : filter.kind === "resources"
                            ? resourcesCount
                            : filtersCount[filter.kind]
                            ? filtersCount[filter.kind]
                            : filter.count,
                      };
                    });
                  }
                  this.setState({
                    allSearchResults: ArraysHelper.unionBy(
                      this.state.allSearchResults,
                      results.searchItems,
                      "id",
                    ) as CarouselItem[],
                    loading: false,
                  });

                  this.handleSearchResultFormat();
                  if (this.filters.length <= 0) {
                    feedBackMessage = "No Results Found!";
                  }
                }

                this.setState({
                  feedBackMessage,
                  loading: false,
                });
              })
              .catch((err) => {
                this.setState({
                  loading: false,
                  feedBackMessage: "No Results Found!",
                });
              });
          }
        },
      );
    });
  }

  /**
   * handle sorting results
   *
   * @type {function(){}}
   */

  handleSortingResults(event: any, order: string) {
    document.querySelectorAll(".sort-by .sort-type").forEach((item) => {
      item.classList.remove("active");
    });

    event.target.classList.add("active");
    this.setState({
      orderSearchBy: order,
    });
    this.handleSearchResults("SORT");
  }

  /**
   * handle changes happens on hash change
   *
   * @memberof SearchResultsGrid
   */
  handleHashChange() {
    // set loading to true again
    this.setState({ loading: true });
    // clear filters
    this.handleSearchFilters();
    // search again with new values
    this.handleSearchResults();
  }

  /**
   *
   *
   * @memberof SearchResultsGrid
   */
  componentWillUnmount(): void {
    window.removeEventListener("hashchange", this.handleHashChange, false);
  }

  /**
   * A function invoked on filter click
   *
   * @type {function(event: React.MouseEvent<HTMLElement>, index:number))}
   */
  handleFilterClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    const FACET_COUNT: number = this.filters[index].count;
    let feedBackMessage: string = "Try another filter, no results found";
    // reset all previously active filters
    this.activeFilterStateArray = this.activeFilterStateArray.map((filter) => {
      return "";
    });

    // set the currently active filter
    this.activeFilterStateArray[index] = "active";

    const isResourcesSearch: boolean = this.panelFilter.search("resource") !== -1;

    if (FACET_COUNT) {
      this.handleSearchResults("FILTER");
      this.handleSearchResultFormat();
      feedBackMessage = "";
    } else {
      this.handleResetArray();
      this.pagination.noMore = true;
    }

    // update state
    this.setState({
      feedBackMessage,
      activeFilter: [...this.activeFilterStateArray],
    });
  };

  /**
   * Clear filters state (no active filter)
   *
   * @type {(void)}
   */
  handleResetArray() {
    this.pagination = { isOnGoing: false, noMore: false };
    this.setState({ allSearchResults: [] });
  }

  /**
   * Invoked when we have a new search result ready to be formatted and displayed.
   *
   */
  handleSearchResultFormat = () => {
    let feedBackMessage: string = "";

    const formatArrayLength =
      windowSize <= 1024
        ? Math.ceil(this.state.allSearchResults.length / 4)
        : Math.ceil(this.state.allSearchResults.length / 5);

    if (!formatArrayLength) {
      feedBackMessage = "No Results Found";
    }
    this.setState({
      feedBackMessage,
    });
  };

  /**
   * Invoked when the user clicks on a suggestion for the search.
   *
   * @param {React.MouseEvent<HTMLElement>} event The event which triggered the function
   * @param {any} suggestion The suggestion the user clicked on
   */
  handleSearchSuggestionClick = (event: React.MouseEvent<HTMLElement>, suggestion: string) => {
    this.setState(
      {
        searchValue: suggestion,
      },
      () => {
        if (this.panelFilter) {
          // if the search is triggered from a certain panel
          Router.to("search", [
            `query=${suggestion}`,
            `searchPhrase=${this.state.searchValue}`,
            `searchFilter=${this.panelFilter}`,
          ]);
        } else {
          // the search is triggered from the navbar
          Router.to("search", [`query=${suggestion}`, `searchPhrase=${this.state.searchValue}`]);
        }
      },
    );
  };

  /**
   *
   *
   * @memberof SearchResultsGrid
   */
  handleLoadMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({
      showMoreResults: true,
    });
  };

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="searchResults">
        {this.filters.length > 0 ? (
          <div className="filterUx-div">
            <div className="intro">
              <span className="text-uppercase title">
                Search Results for: {this.state.searchValue}
                {this.tagFilter ? <span>, & Tag: {this.tagFilter}</span> : null}
              </span>
            </div>
            <ul className="nav nav-tabs panel-nav filters">
              <div className="filters-container">
                {this.filters.map((filterObject: FiltersArray, index: number) => {
                  return (
                    <li
                      key={index}
                      className={`nav-item ${this.state.activeFilter[index]}`}
                      onClick={(event: React.MouseEvent<HTMLElement>) => this.handleFilterClick(event, index)}
                    >
                      {filterObject.name}
                      {filterObject.count > 0 ? <span className="count">{filterObject.count}</span> : null}
                    </li>
                  );
                })}
                <div className="sort-by sort">
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn btn-secondary dropdown-toggle dropdown-button"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      Sort By
                      {SVG.searchDrop}
                    </button>
                    <div className="dropdown-menu">
                      <button
                        className="dropdown-item sort-type"
                        type="button"
                        onClick={() => this.handleSortingResults(event, ODRED_KIND.latest)}
                      >
                        Latest First
                      </button>
                      <button
                        className="dropdown-item sort-type"
                        type="button"
                        onClick={() => this.handleSortingResults(event, ODRED_KIND.oldest)}
                      >
                        Oldest First
                      </button>
                      <button
                        className="dropdown-item sort-type"
                        type="button"
                        onClick={() => this.handleSortingResults(event, ODRED_KIND.AtoZ)}
                      >
                        Alphabetically
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </ul>
          </div>
        ) : null}

        <div className="search-results-grid search-results-componentUx" style={{ position: "relative" }}>
          {/* {this.state.searchResultsFormatted.map(
            (resultArray: any, index: number) => {
              return ( */}
          {/* <div key={index} className="no-hover"> */}
          <GridView
            isOwner={false}
            itemsToShow={6}
            gridContent={this.state.loading && !this.state.allSearchResults.length ? null : this.state.allSearchResults}
            hover={true}
            isMedia={true}
          />
          {/* </div> */}
          {/* );
            }sss
          )} */}
          <p className="no-results"> {this.state.feedBackMessage}</p>
        </div>
      </div>
    );
  }
}
